import React, { FC, useContext, useState } from "react";
import { Popover, Theme, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { ReactComponent as BiologicsSVG } from "../../svgs/biologics.svg";
import { VisibilityContext } from "../../context";
import { ComplexFormTypeDescription, RxStrategyPipelines } from "../../typescript/interfaces/data";
import Hyperlink from "../Common/Hyperlink";
import Ellipsis from "../Common/Ellipsis";
import config from "../../config";
import StyledTooltip from "../Common/StyledTooltip";
import HelpIcon from "@material-ui/icons/Help";

const useStyles = makeStyles((theme: Theme) => ({
  brandDrug: {
    display: "flex",
    flexWrap: "wrap",
    whiteSpace: "break-spaces",
    position: "relative",
  },
  biologics: {
    position: "absolute",
    bottom: 1,
    marginLeft: "4px",
    height: "18px",
    alignContent: "flex-end",
  },
  overflowNote: {
    display: "inline",
    position: "absolute",
    bottom: "0px",
    fontSize: "0.7rem",
    fontStyle: "italic",
    color: "lightslategray",
    cursor: "pointer",
  },
  plusSign: {
    position: "absolute",
    left: "-8px",
    bottom: "0px",
    fontSize: "0.7rem",
    fontStyle: "italic",
    color: "lightslategray",
    cursor: "pointer",
  },
  complexTag: {
    fontWeight: "bold",
    color: "#ff8f00",
    cursor: "pointer",
    fontSize: 12,
  },
  complexTooltip: {
    maxWidth: 400,
  },
  helpIcon: {
    color: theme.palette.primary.main,
    cursor: "default",
    marginLeft: "6px",
  },
}));

type DrugNameTDProps = {
  drugId: number;
  isBiologic: boolean;
  brandName: string;
  genericName: string;
  complexForms: ComplexFormTypeDescription[];
  likelyComplexForms: ComplexFormTypeDescription[];
  width: number;
  notesAndCommentary: RxStrategyPipelines[];
  hasOverflowNote: boolean;
  rowId: number;
  handleDetailPanelOpen: (rowData: any) => void;
  noLink: boolean;
};

const DrugNameTD: FC<DrugNameTDProps> = ({
  drugId,
  isBiologic,
  brandName,
  genericName,
  complexForms,
  likelyComplexForms,
  width,
  notesAndCommentary,
  hasOverflowNote,
  rowId,
  handleDetailPanelOpen,
  noLink,
}) => {
  const classes = useStyles();
  const [visibilityContext] = useContext(VisibilityContext);
  const [el, setEl] = useState<(EventTarget & HTMLAnchorElement) | null>(null);

  const hasPharmacistCommentary = notesAndCommentary.map((strategy) => strategy.comment).filter(Boolean).length > 0;
  const showComplexTag =
    complexForms.length > 0 || (config.featureToggles.likelyComplexFormReleased && likelyComplexForms.length > 0);
  return (
    <>
      <span className={classes.brandDrug}>
        <Hyperlink url={`${config.USER_URL}/Pharma/Drug/${drugId}`} noLink={noLink}>
          <Typography variant="body2">
            <Ellipsis width={width - 25} variant="body2" text={brandName}>
              {brandName}
              {isBiologic && <BiologicsSVG className={classes.biologics} />}
            </Ellipsis>
          </Typography>
        </Hyperlink>
      </span>
      <Ellipsis variant="body2" text={genericName} width={width}>
        {genericName}
      </Ellipsis>
      {showComplexTag && (
        <>
          <a className={classes.complexTag} onClick={(e) => setEl(e.currentTarget)}>
            COMPLEX
          </a>
          <Popover
            open={Boolean(el)}
            anchorEl={el}
            onClose={() => setEl(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
          >
            <div style={{ padding: "1rem" }}>
              {complexForms.map((x) => {
                return (
                  <div key={x.complexForm} style={{ display: "flex", alignItems: "center" }}>
                    <span>{x.name}</span>
                    <StyledTooltip
                      title={x.description}
                      placement="right"
                      classes={{ tooltip: classes.complexTooltip }}
                    >
                      <HelpIcon fontSize="small" className={classes.helpIcon} />
                    </StyledTooltip>
                  </div>
                );
              })}
              {config.featureToggles.likelyComplexFormReleased &&
                likelyComplexForms.map((x) => {
                  return (
                    <div key={x.complexForm} style={{ display: "flex", alignItems: "center" }}>
                      <span>{x.name} *</span>
                      <StyledTooltip
                        title={<span style={{ whiteSpace: "pre-line" }}>{x.description}</span>}
                        classes={{ tooltip: classes.complexTooltip }}
                        placement="right"
                      >
                        <HelpIcon fontSize="small" className={classes.helpIcon} />
                      </StyledTooltip>
                    </div>
                  );
                })}
            </div>
          </Popover>
        </>
      )}
      {hasPharmacistCommentary && hasOverflowNote && (
        <div onClick={() => handleDetailPanelOpen(rowId)}>
          <Typography variant="body2" className={classes.plusSign}>
            {visibilityContext.detailPanelsOpen.includes(rowId) ? "-" : "+"}
          </Typography>
          <Typography variant="body2" className={classes.overflowNote}>
            Pharmacist Commentary
          </Typography>
        </div>
      )}
    </>
  );
};

export default DrugNameTD;
